import type { StageResult, ValidationIssue, BlockBundle } from "../types"
import { ACTION_DEFS } from "@cogo/action-registry"
import { runActionFlowDeepRules, OPERATION_REQUIRED_PARAMS } from "./stage-actionflow-deep"
import { expandDottedStateKeys } from "./state-key-prefixes"
import { normalizeEventBindings } from "../utils/event-bindings"
import { resolveRuntimeAction } from "../vocab/runtime-action-resolution"
import {
  AUTO_CAPTURE_ACTIONS,
  DEFAULT_RESULT_SLOTS,
  RESULT_TARGET_PARAMS,
  namedResultSlot,
  UNCONDITIONAL_RESULT_SLOTS,
} from "../vocab/runtime-result-writes"
import {
  STRUCTURAL_STEP_TYPES,
  autoCapturesStepName,
  declaredStateTargets,
  isRuntimeStepType,
  isSourceTypedActionStepType,
  stepArgumentSurface,
  stepDispatchedAction,
} from "../vocab/runtime-step-vocabulary"

// ── Wave 9A — Canonical expression prefix vocabulary ─────────────────────────
// The vocabulary itself lives in ONE place: src/vocab/expression-namespaces.ts,
// where the canonical 9 are derived from the ontology TTL rather than typed.
// These re-exports keep the long-standing public surface (src/index.ts exports
// CANONICAL_EXPR_PREFIXES / LEGACY_EXPR_PREFIXES from this module) without
// restating a single member.
export {
  CANONICAL_EXPR_PREFIXES,
  PRESERVED_EXPR_PREFIXES,
  LEGACY_EXPR_PREFIXES,
} from "../vocab/expression-namespaces"

import {
  CANONICAL_EXPR_PREFIXES,
  PRESERVED_EXPR_PREFIXES,
  LEGACY_EXPR_PREFIXES,
  KNOWN_EXPRESSION_ROOTS,
} from "../vocab/expression-namespaces"

/**
 * Scan a string value for expression prefixes, categorised by tier.
 *
 * MEASURED BLINDNESS — do not read a clean `expression-prefix-legacy` result
 * as "the corpus has no legacy prefixes" (2026-08-26 sweep, blocks-v2, 253
 * blocks):
 *
 *   legacy prefixes present as a ROOT identifier inside some `#{…}`:
 *     state 52 (15 blocks) · localState 14 (1) · ui 7 (2) · response 1 (1)
 *     = 74 across 19 blocks
 *   legacy prefixes this function reports:            0
 *
 * The regex below anchors the prefix to the FIRST character-run after `#{`
 * and forbids `}` before the close (`[^}]+`). Every one of those 74 sits
 * behind an operator or a bracket — `#{!state.showMoreFilters}`,
 * `#{[...state.files, …]}`, `#{local.homePrice * state.downPaymentPercent /
 * 100}`, `#{survey.answers[ui.currentQuestion.id]}` — so none match. The two
 * callers narrow it further: `analyzeUiExpressionPrefixes` walks only
 * `ui.root`, and `analyzeExpressionPrefixes` only flow `step.params`, so
 * `variables.cogo.json` and `datasource.cogo.json` are never scanned at all
 * (24 and 7 of the occurrences respectively live there).
 *
 * Widening this is NOT a local edit: the rule is `error` level in two
 * safety:true stages, so reporting those 74 would move ~19 blocks M3 -> M2
 * in one step. It needs to be sequenced with the corpus normalisation, not
 * switched on. Runtime behaviour is unaffected either way — SymbolStore's
 * `LEGACY_READ_ALIAS` maps `state`/`localState` -> `local` on READ (verified:
 * `#{state.amenities}` and `#{local.amenities}` return the same object), which
 * is exactly why this drifted silently.
 */
export function scanExpressionPrefixes(value: string): { canonical: string[]; preserved: string[]; legacy: string[] } {
  const out = { canonical: [] as string[], preserved: [] as string[], legacy: [] as string[] }
  if (typeof value !== "string") return out
  const rx = /#\{([a-zA-Z_][a-zA-Z0-9_]*)(?:\.[^}]+)?\}/g
  let m: RegExpExecArray | null
  while ((m = rx.exec(value))) {
    const p = m[1]
    if (CANONICAL_EXPR_PREFIXES.has(p)) out.canonical.push(p)
    else if (PRESERVED_EXPR_PREFIXES.has(p)) out.preserved.push(p)
    else if (p in LEGACY_EXPR_PREFIXES) out.legacy.push(p)
    // else: unknown prefix — existing `knownNamespaces` logic handles those
  }
  return out
}

function forEachNestedStepBranch(
  step: Record<string, unknown>,
  visit: (steps: Record<string, unknown>[], branchPath: string) => void,
): void {
  for (const field of ["then", "else", "steps", "try", "catch", "finally", "default", "onEnter", "onSuccess", "onError", "onFinally", "compensate", "stages"] as const) {
    if (Array.isArray(step[field])) {
      visit(step[field] as Record<string, unknown>[], field)
    }
  }
  if (step.cases && typeof step.cases === "object" && !Array.isArray(step.cases)) {
    for (const [caseName, branch] of Object.entries(step.cases as Record<string, unknown>)) {
      if (Array.isArray(branch)) {
        visit(branch as Record<string, unknown>[], `cases.${caseName}`)
      }
    }
  }
}

// ─────────────────────────────────────────────────────────────────────────────

export function runActionFlowStage(bundle: BlockBundle): StageResult {
  const start = Date.now()
  const issues: ValidationIssue[] = []
  validateDirectEventBindings(issues, bundle)

  const flows = (bundle.flow as Record<string, unknown>)?.flows
  if (!flows || typeof flows !== "object") {
    return {
      id: "actionflow",
      name: "ActionFlow Analysis",
      passed: !issues.some((issue) => issue.level === "error"),
      duration: Date.now() - start,
      issues,
    }
  }

  const state = ((bundle.variables as Record<string, unknown>)?.state ?? {}) as Record<string, unknown>
  const varLocalState = ((bundle.variables as Record<string, unknown>)?.localState ?? {}) as Record<string, unknown>
  // Expand flat-dotted state keys into namespace prefixes so lookups like
  // stateKeys.has('abcAnalysis') resolve when state.json declares
  // 'abcAnalysis.rows' / 'abcAnalysis.pageIndex' / etc.
  const stateKeys = expandDottedStateKeys([...Object.keys(state), ...Object.keys(varLocalState)])
  // Copy #3 of 5 (pre-fold): a hand-typed list 78 lines below the canonical set
  // in this same file, which omitted `local` and `props`. It is now the one
  // composed vocabulary plus this block's own declared state keys.
  const knownNamespaces = new Set([...stateKeys, ...KNOWN_EXPRESSION_ROOTS])

  // Add block namespace as a valid scope (e.g., #{merchantManagement.field})
  const blockNamespace = (bundle.block as Record<string, unknown>)?.namespace as string | undefined
  if (blockNamespace) knownNamespaces.add(blockNamespace)

  // Add localState keys from block.json stateContract
  const blockStateContract = (bundle.block as Record<string, unknown>)?.stateContract as Record<string, unknown> | undefined
  const blockLocalState = blockStateContract?.localState as Record<string, unknown> | undefined
  if (blockLocalState && typeof blockLocalState === "object") {
    for (const key of Object.keys(blockLocalState)) {
      knownNamespaces.add(key)
    }
  }

  // Collect iterateOver itemVar aliases from UI tree as valid namespaces
  const uiRoot = (bundle.ui as Record<string, unknown>)?.root as Record<string, unknown> | undefined
  if (uiRoot) {
    walkUiTree(uiRoot, (node) => {
      const itemVar = node.itemVar as string | undefined
      if (itemVar) knownNamespaces.add(itemVar)
    })
  }

  for (const [flowName, flowDef] of Object.entries(flows as Record<string, unknown>)) {
    if (typeof flowDef !== "object" || flowDef === null) continue
    const fd = flowDef as Record<string, unknown>
    const steps = fd.steps
    if (!Array.isArray(steps) || steps.length === 0) continue
    analyzeSteps(issues, flowName, steps as Record<string, unknown>[], knownNamespaces)
    analyzeExpressionPrefixes(issues, flowName, steps as Record<string, unknown>[])
  }

  // ── Connection validation (cross-file) ──
  validateFlowEventBindings(issues, flows as Record<string, unknown>, bundle)
  validateCallFlowReferences(issues, flows as Record<string, unknown>)
  validateCallFlowRefWithinBlock(issues, flows as Record<string, unknown>, bundle)
  validateResultTargetUsage(issues, flows as Record<string, unknown>)
  validateActionTypeValid(issues, flows as Record<string, unknown>)
  validateEmitListenerCoherence(issues, flows as Record<string, unknown>, bundle)

  // Deep rules (runtime compatibility checks)
  const deepIssues = runActionFlowDeepRules(
    flows as Record<string, unknown>,
    state,
  )
  issues.push(...deepIssues)

  const hasErrors = issues.some(i => i.level === "error")
  return {
    id: "actionflow",
    name: "ActionFlow Analysis",
    passed: !hasErrors,
    duration: Date.now() - start,
    issues,
  }
}

/**
 * ActionFlow stage — DIRECT EVENT→ACTION BINDING subset (RETEST6 wall ③,
 * stage-seam repair, 2026-08-18).
 *
 * A DIFFERENT family from the oracle-blindness subsets (G1/J3/#2199/graph-
 * rules): there the error was never delivered; here it was delivered — to the
 * WRONG EDITOR. `runActionFlowStage` opens with `validateDirectEventBindings`,
 * a check whose inputs are `bundle.events` + `bundle.ui` ONLY
 * (`collectEventBindings` + the static `@cogo/action-registry` ACTION_DEFS —
 * it never reads `bundle.flow`). Those inputs are COMPLETE at the EVENTS
 * stage of generation, where events.json is still editable. But the events
 * oracle (`validateEvents` in `@cogo/agentic-generator`) was shape-only, so
 * the check first ran inside the FLOW stage's full-bundle re-validation —
 * delivering events-side errors to a loop that can only rewrite flow.json.
 *
 * Measured (RETEST6 §4.3, committed receipt at super-repo
 * `docs/superpowers/reports/2026-08-18-wonbanking-retest6/receipts/
 * DETAIL02-flow-rounds.txt`): wb1r10-detail-02's `wb1-account-detail-panel`
 * died at flow after 3 rounds on 8 identical per-round errors —
 *
 *   [actionflow-direct-action-required-param] Event 'acct-no-value:tap'
 *   direct action 'copyToClipboard' is missing required param(s): text.
 *
 * — the frozen stage-2 events wrote `params.value` where the registry
 * requires `text`. Un-repairable by construction: 3 rounds burned, block
 * dead, 6 requirement-instances lost.
 *
 * This subset gives the EVENTS stage the same check through the SAME
 * `validateDirectEventBindings` helper (one emission site — the full stage
 * keeps running it at the flow stage as defense in depth; this runner only
 * moves FIRST delivery to the artifact's owning stage). Emitted rules:
 * `actionflow-event-binding-target`, `actionflow-legacy-action-alias`,
 * `actionflow-direct-action-unknown`, `actionflow-direct-action-non-atomic`,
 * `actionflow-setState-params-complete`,
 * `actionflow-direct-action-required-param`, `actionflow-action-param-enum`.
 * Flow-referencing bindings (`{flow: name}`) are untouched — the flow-map
 * closure stays with `runContractEventFlowRefStage` at the flow stage, where
 * the flow map exists.
 */
export function runActionflowEventBindingsStage(bundle: BlockBundle): StageResult {
  const start = Date.now()
  const issues: ValidationIssue[] = []
  validateDirectEventBindings(issues, bundle)
  return {
    id: "actionflow",
    name: "ActionFlow Analysis — direct event→action binding subset",
    passed: !issues.some((issue) => issue.level === "error"),
    duration: Date.now() - start,
    issues,
  }
}

function validateDirectEventBindings(issues: ValidationIssue[], bundle: BlockBundle): void {
  for (const { eventKey, binding, file } of collectEventBindings(bundle)) {
    const flow = typeof binding.flow === "string" && binding.flow.length > 0
    const action = typeof binding.action === "string" && binding.action.length > 0

    if (flow === action) {
      issues.push({
        stage: "actionflow",
        level: "error",
        file,
        rule: "actionflow-event-binding-target",
        message: `Event '${eventKey}' must target exactly one of 'flow' or atomic 'action'.`,
      })
      continue
    }
    if (!action) continue

    const actionName = binding.action as string
    const resolvedAction = resolveRuntimeAction(actionName)
    const canonicalAction = resolvedAction?.canonicalAction ?? actionName
    const definition = Object.prototype.hasOwnProperty.call(ACTION_DEFS, canonicalAction)
      ? ACTION_DEFS[canonicalAction]
      : undefined
    if (resolvedAction?.isAlias) {
      issues.push({
        stage: "actionflow",
        level: "warning",
        file,
        rule: "actionflow-legacy-action-alias",
        message: `Event '${eventKey}' uses legacy action alias '${actionName}'. Use canonical '${canonicalAction}' in new bindings.`,
      })
    }
    if (!definition && !resolvedAction) {
      issues.push({
        stage: "actionflow",
        level: "error",
        file,
        rule: "actionflow-direct-action-unknown",
        message: `Event '${eventKey}' uses unknown direct action '${actionName}'.`,
      })
    } else if (!(definition?.semantics.atomic ?? resolvedAction?.grounding.semantics.atomic)) {
      issues.push({
        stage: "actionflow",
        level: "error",
        file,
        rule: "actionflow-direct-action-non-atomic",
        message: `Event '${eventKey}' cannot directly execute orchestration action '${actionName}'. Put it in an ActionFlow.`,
      })
    } else {
      const params = binding.params && typeof binding.params === "object" && !Array.isArray(binding.params)
        ? binding.params as Record<string, unknown>
        : {}
      const operation = typeof params.operation === "string" ? params.operation : undefined
      if (definition && operation !== undefined) {
        // Operation-polymorphic action (currently only setState): required
        // params depend on `operation`, not on a fixed per-action list.
        // ACTION_DEFS' flat `required: true` flag on `value` describes the
        // DEFAULT (no-operation / plain replace) shape only — enforcing it
        // unconditionally here would reject e.g. `operation: "updateWhere"`
        // for never carrying a `value` it never reads at runtime
        // (action-flow-engine.ts#actionSetState), purely because the call
        // happens to be bound directly to an event instead of wrapped in a
        // named flow. Any explicit non-"set" `operation` exempts `value`;
        // when the operation has a known required-param set (the same
        // OPERATION_REQUIRED_PARAMS table the flow-embedded step check in
        // stage-actionflow-deep.ts uses), check that instead, at the SAME
        // warning severity, so a setState call is judged identically
        // regardless of binding style. `target` remains unconditionally
        // required (every operation reads it) via the flat check below.
        const operationRequiredParams = OPERATION_REQUIRED_PARAMS[operation]
        if (operationRequiredParams) {
          const missingForOp = operationRequiredParams.filter((name) => !(name in params))
          if (missingForOp.length > 0) {
            issues.push({
              stage: "actionflow",
              level: "warning",
              file,
              rule: "actionflow-setState-params-complete",
              message: `Event '${eventKey}' direct action '${actionName}' operation '${operation}' missing required param(s): ${missingForOp.join(", ")}.`,
            })
          }
        }
        const missingAlways = definition.params
          .filter((param) => param.required && param.name !== "value" && !(param.name in params))
          .map((param) => param.name)
        if (missingAlways.length > 0) {
          issues.push({
            stage: "actionflow",
            level: "error",
            file,
            rule: "actionflow-direct-action-required-param",
            message: `Event '${eventKey}' direct action '${actionName}' is missing required param(s): ${missingAlways.join(", ")}.`,
          })
        }
      } else if (definition) {
        const missing = definition.params.filter((param) => param.required && !(param.name in params)).map((param) => param.name)
        if (missing.length > 0) {
          issues.push({
            stage: "actionflow",
            level: "error",
            file,
            rule: "actionflow-direct-action-required-param",
            message: `Event '${eventKey}' direct action '${actionName}' is missing required param(s): ${missing.join(", ")}.`,
          })
        }
      }
      if (definition) {
        validateActionParamEnums(issues, definition, params, `Event '${eventKey}'`, file)
      }
    }
  }
}

function collectEventBindings(bundle: BlockBundle): Array<{
  eventKey: string
  binding: Record<string, unknown>
  file: string
}> {
  const collected: Array<{ eventKey: string; binding: Record<string, unknown>; file: string }> = []
  const add = (eventKey: string, candidate: unknown, file: string): void => {
    if (candidate && typeof candidate === "object" && !Array.isArray(candidate)) {
      collected.push({ eventKey, binding: candidate as Record<string, unknown>, file })
    }
  }
  const addEventMap = (elementId: string, eventMap: unknown, file: string): void => {
    if (!eventMap || typeof eventMap !== "object" || Array.isArray(eventMap)) return
    for (const [eventType, candidate] of Object.entries(eventMap as Record<string, unknown>)) {
      add(`${elementId}:${eventType}`, candidate, file)
    }
  }

  const events = bundle.events as Record<string, unknown> | undefined
  const bindings = events?.bindings
  if (bindings && typeof bindings === "object" && !Array.isArray(bindings)) {
    for (const [eventKey, candidate] of Object.entries(bindings as Record<string, unknown>)) {
      add(eventKey, candidate, "events.json")
    }
  }
  if (events) {
    for (const [elementId, eventMap] of Object.entries(events)) {
      if (["bindings", "elements", "events", "lifecycle", "schemaVersion", "blockType"].includes(elementId)) continue
      addEventMap(elementId, eventMap, "events.json")
    }
    for (const wrapper of [events.elements, events.events]) {
      if (wrapper && typeof wrapper === "object" && !Array.isArray(wrapper)) {
        for (const [elementId, eventMap] of Object.entries(wrapper as Record<string, unknown>)) {
          addEventMap(elementId, eventMap, "events.json")
        }
      }
    }
  }

  const uiRoot = (bundle.ui as Record<string, unknown> | undefined)?.root as Record<string, unknown> | undefined
  if (uiRoot) {
    walkUiTree(uiRoot, (node) => {
      const elementId = typeof node.id === "string" ? node.id : "unknown"
      const inlineEvents = node.events
      if (!inlineEvents || typeof inlineEvents !== "object" || Array.isArray(inlineEvents)) return
      for (const [eventType, candidate] of Object.entries(inlineEvents as Record<string, unknown>)) {
        add(`${elementId}:${eventType}`, candidate, "ui.json")
      }
    })
  }
  return collected
}

function validateActionParamEnums(
  issues: ValidationIssue[],
  definition: (typeof ACTION_DEFS)[string],
  params: Record<string, unknown>,
  reference: string,
  file: string,
): void {
  for (const param of definition.params) {
    if (!param.enumValues || !(param.name in params)) continue
    const value = params[param.name]
    const dynamic = typeof value === "string" && (value.includes("#{") || value.includes("@{"))
    if (dynamic || (typeof value === "string" && param.enumValues.includes(value))) continue
    issues.push({
      stage: "actionflow",
      level: "error",
      file,
      rule: "actionflow-action-param-enum",
      message: `${reference} action '${definition.name}' param '${param.name}' must be one of: ${param.enumValues.join(", ")}. Received '${String(value)}'.`,
    })
  }
}

// ── Layer-2 validate-action contract (2026-08-18, RETEST6 §4.2) ──────────────
// #2202 fixed the enum's TOP-LEVEL declaration (validate executes
// `params.target` + `params.rules` — fields[]/errorTarget were phantoms).
// These checks close the SECOND layer: the shapes INSIDE those two params,
// derived from the executor's bytes (`actionValidate`,
// cogo-runtime-v2/src/action-flow-engine.ts — reads `rule.type` to dispatch,
// `rule.value` as the ONLY per-type parameter key, `rule.message` as the
// failure message; unknown types and rules without a usable value degenerate
// to never-fail / always-fail guards). The table below is pinned against the
// engine source by src/__tests__/validate-rule-key-contract-pin.test.ts —
// if the executor's rule loop changes, that pin goes red before this table
// can drift.
//
// Measured defect this delivers to the retry loop (wb1r10, all 3 detail
// runs — super-repo docs/superpowers/reports/2026-08-18-wonbanking-retest6/
// receipts/DETAIL-setstate-issues.txt): target carried a computed expression
// (`#{appData.acctNo.replace(/-/g, '')}`) that fails the M5 graph rule and
// blinds the ruler's field extraction, and the rule entry carried its regex
// under `pattern` where the executor reads `value` — a guard that can never
// fail. graph-rules is NOT in the retry loop's oracle; this stage IS.
export const VALIDATE_RULE_KEY_CONTRACT: Readonly<
  Record<string, { readonly valueKey: "value" | null; readonly missingValueBehavior: "never-fails" | "always-fails" | null }>
> = {
  // required/email read no parameter key — target alone decides.
  required: { valueKey: null, missingValueBehavior: null },
  email: { valueKey: null, missingValueBehavior: null },
  // pattern: String(rule.value ?? "") → RegExp("") matches everything.
  pattern: { valueKey: "value", missingValueBehavior: "never-fails" },
  // length/number comparisons: Number(undefined) = NaN → comparison false.
  minLength: { valueKey: "value", missingValueBehavior: "always-fails" },
  maxLength: { valueKey: "value", missingValueBehavior: "always-fails" },
  min: { valueKey: "value", missingValueBehavior: "always-fails" },
  max: { valueKey: "value", missingValueBehavior: "always-fails" },
}

/** Keys the executor reads on EVERY rule entry, regardless of type. */
const VALIDATE_RULE_UNIVERSAL_KEYS = new Set(["type", "message"])

/** A plain dotted symbol path — what target positions carry. */
const PLAIN_SYMBOL_PATH = /^[A-Za-z_$][A-Za-z0-9_$]*(\.[A-Za-z_$][A-Za-z0-9_$]*)*$/

/**
 * Longest leading dotted-path prefix of an expression, with a trailing
 * method-call segment stripped: `appData.acctNo.replace(/-/g, '')` →
 * `appData.acctNo`. Returns undefined when the expression has no path head.
 */
function expressionBasePath(expr: string): string | undefined {
  const m = /^[A-Za-z_$][A-Za-z0-9_$]*(\.[A-Za-z_$][A-Za-z0-9_$]*)*/.exec(expr)
  if (!m) return undefined
  let head = m[0]
  // The segment immediately followed by `(` is a method name, not a path leg.
  if (expr.slice(head.length).startsWith("(")) {
    const lastDot = head.lastIndexOf(".")
    if (lastDot === -1) return undefined
    head = head.slice(0, lastDot)
  }
  return head
}

function analyzeValidateStep(
  issues: ValidationIssue[],
  flowName: string,
  i: number,
  step: Record<string, unknown>,
): void {
  const params = step.params as Record<string, unknown> | undefined
  if (!params || typeof params !== "object") return

  // (1) target must be a symbol path, never a computed expression. Two gates
  // read the RAW string — the M5 graph rule (setState-target-undeclared) and
  // the measurement's field extraction — and neither can see a symbol inside
  // an expression. A #{...}-wrapped PLAIN path is left alone here: the
  // M5-vs-runtime disagreement on wrapping is a separate adjudication; this
  // rule owns the measured class (method calls / operators in the target).
  const target = params.target
  if (typeof target === "string" && target.length > 0) {
    let inner = target.trim()
    if (inner.startsWith("#{") && inner.endsWith("}")) {
      inner = inner.slice(2, -1).trim()
    }
    if (!PLAIN_SYMBOL_PATH.test(inner)) {
      const basePath = expressionBasePath(inner)
      issues.push({
        stage: "actionflow",
        level: "error",
        file: "flow.json",
        rule: "actionflow-validate-target-expression",
        message:
          `Flow '${flowName}' step[${i}] validate target '${target}' is a computed expression, not a symbol path. ` +
          `Run the transform in a prior compute step (e.g. { "action": "compute", "params": { "target": "<newKey>", "expression": "#{${inner}}" } }, ` +
          `with <newKey> declared in variables.state), then validate { "target": "<newKey>" }` +
          (basePath ? ` — the value under validation reads from '${basePath}'` : "") +
          ".",
        metadata: {
          target,
          expression: inner,
          ...(basePath ? { basePath } : {}),
          repair: "compute-then-validate",
        },
      })
    }
  }

  // (2) rule entries must carry the executor's per-type keys.
  const rules = params.rules
  if (Array.isArray(rules)) {
    if (rules.length === 0) {
      issues.push({
        stage: "actionflow",
        level: "warning",
        file: "flow.json",
        rule: "actionflow-validate-rules-empty",
        message: `Flow '${flowName}' step[${i}] validate has an empty rules array — actionValidate returns valid:true unconditionally, so the step is decoration.`,
      })
    }
    rules.forEach((entry, ruleIndex) => {
      if (!entry || typeof entry !== "object" || Array.isArray(entry)) return
      const rule = entry as Record<string, unknown>
      const t = rule.type
      if (typeof t !== "string" || t.length === 0) {
        issues.push({
          stage: "actionflow",
          level: "error",
          file: "flow.json",
          rule: "actionflow-validate-rule-type-missing",
          message: `Flow '${flowName}' step[${i}] validate rules[${ruleIndex}] has no 'type' — the executor dispatches on rule.type, so this entry falls to the silent-pass default and can never fail.`,
          metadata: { ruleIndex },
        })
        return
      }
      const contract = VALIDATE_RULE_KEY_CONTRACT[t]
      if (!contract) {
        issues.push({
          stage: "actionflow",
          level: "error",
          file: "flow.json",
          rule: "actionflow-validate-rule-type-unknown",
          message:
            `Flow '${flowName}' step[${i}] validate rules[${ruleIndex}] type '${t}' has no executor case — it passes silently and can never fail. ` +
            `Executable types: ${Object.keys(VALIDATE_RULE_KEY_CONTRACT).join(" | ")}.`,
          metadata: { ruleIndex, type: t, allowed: Object.keys(VALIDATE_RULE_KEY_CONTRACT) },
        })
        return
      }
      const hasValue = "value" in rule
      const strays = Object.keys(rule).filter(
        (k) => !VALIDATE_RULE_UNIVERSAL_KEYS.has(k) && !(contract.valueKey !== null && k === "value"),
      )
      if (contract.valueKey !== null && !hasValue) {
        if (strays.length > 0) {
          // The wb1r10 shape: the parameter rides under a key the executor
          // never reads (e.g. `pattern`), and `value` is absent entirely.
          for (const strayKey of strays) {
            issues.push({
              stage: "actionflow",
              level: "error",
              file: "flow.json",
              rule: "actionflow-validate-rule-key-unread",
              message:
                `Flow '${flowName}' step[${i}] validate rules[${ruleIndex}] carries '${strayKey}' but the executor reads rule.value for type '${t}' — '${strayKey}' is never read. ` +
                `As emitted the guard can ${contract.missingValueBehavior === "never-fails" ? "never fail (empty-regex/undefined parameter passes everything)" : "never pass (NaN comparison always fails)"}. ` +
                `Rename '${strayKey}' to 'value'.`,
              metadata: { ruleIndex, type: t, strayKey, executedKey: "value" },
            })
          }
        } else {
          issues.push({
            stage: "actionflow",
            level: "error",
            file: "flow.json",
            rule: "actionflow-validate-rule-value-missing",
            message:
              `Flow '${flowName}' step[${i}] validate rules[${ruleIndex}] type '${t}' requires rule.value — without it the guard ` +
              `${contract.missingValueBehavior === "never-fails" ? "can never fail (RegExp('') matches everything)" : "always fails (Number(undefined) = NaN)"}` +
              `.`,
            metadata: { ruleIndex, type: t, executedKey: "value" },
          })
        }
      } else if (strays.length > 0) {
        // Executed key satisfied (or type takes none) — strays are unread
        // decoration; the guard still executes as declared.
        for (const strayKey of strays) {
          issues.push({
            stage: "actionflow",
            level: "warning",
            file: "flow.json",
            rule: "actionflow-validate-rule-key-unread",
            message: `Flow '${flowName}' step[${i}] validate rules[${ruleIndex}] key '${strayKey}' is never read by the executor (type '${t}' reads ${contract.valueKey !== null ? "type/value/message" : "type/message"}) — unread decoration.`,
            metadata: { ruleIndex, type: t, strayKey, ...(contract.valueKey !== null ? { executedKey: "value" } : {}) },
          })
        }
      }
    })
  }
}

function analyzeSteps(
  issues: ValidationIssue[],
  flowName: string,
  steps: Record<string, unknown>[],
  knownNamespaces: Set<string>,
): void {
  for (let i = 0; i < steps.length; i++) {
    const step = steps[i]
    if (!step || typeof step !== "object") continue

    // Infer implicit basic type (steps with action but no type)
    const effectiveType = step.type ?? (step.action ? "basic" : undefined)
    const stepAction = typeof step.action === "string" ? step.action : undefined
    const resolvedAction = stepAction ? resolveRuntimeAction(stepAction) : undefined
    const canonicalAction = resolvedAction?.canonicalAction ?? stepAction ?? ""

    // Basic step without action
    if (effectiveType === "basic" && !step.action) {
      issues.push({
        stage: "actionflow",
        level: "error",
        file: "flow.json",
        rule: "actionflow-missing-action",
        message: `Flow '${flowName}' step[${i}] (basic) has no action`,
      })
    }

    // Conditional without condition
    if (effectiveType === "conditional" && !step.condition) {
      issues.push({
        stage: "actionflow",
        level: "error",
        file: "flow.json",
        rule: "actionflow-missing-condition",
        message: `Flow '${flowName}' step[${i}] (conditional) has no condition`,
      })
    }

    // Loop without over/collection
    if (effectiveType === "loop" && !step.over && !step.collection) {
      issues.push({
        stage: "actionflow",
        level: "error",
        file: "flow.json",
        rule: "actionflow-missing-loop-source",
        message: `Flow '${flowName}' step[${i}] (loop) has no 'over' or 'collection'`,
      })
    }

    // Deprecated action detection (generic — any action with deprecated: true in ACTION_DEFS)
    if (stepAction) {
      const actionDef = ACTION_DEFS[canonicalAction]
      if (actionDef?.deprecated) {
        issues.push({
          stage: "actionflow",
          level: "warning",
          file: "flow.json",
          rule: "actionflow-deprecated-action",
          message: `Action '${stepAction}' is deprecated${actionDef.replacedBy ? ` — migrate to '${actionDef.replacedBy}'` : ""} (at flows.${flowName}.steps[${i}])`,
        })
      }
    }

    // callFlow: must use 'flow' or 'flowName', not 'flowId'
    if (step.action === "callFlow") {
      const cfParams = step.params as Record<string, unknown> | undefined
      if (cfParams) {
        if (!cfParams.flow && !cfParams.flowName) {
          const hasFlowId = "flowId" in cfParams
          issues.push({
            stage: "actionflow",
            level: "error",
            file: "flow.json",
            rule: "actionflow-callflow-invalid-param",
            message: `Flow '${flowName}' step[${i}] callFlow ${hasFlowId ? "uses 'flowId' (invalid) — use 'flow' or 'flowName'" : "missing 'flow' or 'flowName' param"}`,
          })
        }
      }
    }

    // Check upsert operations for wrong parameter names
    if (canonicalAction === "supabaseUpsert" || canonicalAction === "restApi" || canonicalAction === "supabaseQuery") {
      const upsertParams = step.params as Record<string, unknown> | undefined
      if (upsertParams) {
        const operation = upsertParams.operation as string | undefined
        if (operation === "upsert") {
          const UPSERT_RENAMES: Record<string, string> = {
            insert: "onNew",
            update: "onMatch",
          }
          for (const [wrongKey, correctKey] of Object.entries(UPSERT_RENAMES)) {
            if (wrongKey in upsertParams) {
              issues.push({
                stage: "actionflow",
                level: "warning",
                file: "flow.json",
                rule: "actionflow-upsert-wrong-param",
                message: `Flow '${flowName}' upsert uses '${wrongKey}' but runtime expects '${correctKey}'. Rename to fix.`,
              })
            }
          }
          // Also detect flat "value" instead of onNew/onMatch pair
          if ("value" in upsertParams && !("onNew" in upsertParams) && !("onMatch" in upsertParams)) {
            issues.push({
              stage: "actionflow",
              level: "warning",
              file: "flow.json",
              rule: "actionflow-upsert-wrong-param",
              message: `Flow '${flowName}' upsert uses flat 'value' but runtime expects 'onNew'/'onMatch' pair. Restructure to fix.`,
            })
          }
        }
      }
    }

    // v5.8: Check setState target — must NOT use "local." or "localState." prefix.
    // Bare keys are correct. Runtime `#{X}` UI bindings read from the same namespace
    // that bare setState writes to. Prefixing with "local." writes to a DIFFERENT
    // namespace and the UI never sees the update.
    if (canonicalAction === "setState") {
      const setParams = step.params as Record<string, unknown> | undefined
      const target = setParams?.target as string | undefined
      if (target && typeof target === "string" && !target.includes("#{")) {
        if (target.startsWith("local.")) {
          const stripped = target.slice("local.".length)
          issues.push({
            stage: "actionflow",
            level: "error",
            file: "flow.json",
            rule: "actionflow-setstate-local-prefix",
            message: `Flow '${flowName}' step[${i}] setState target '${target}' must NOT use 'local.' prefix — use '${stripped}' (bare key). 'local.X' writes to a different namespace than UI bindings read.`,
          })
        }
        if (target.startsWith("localState.")) {
          const stripped = target.slice("localState.".length)
          issues.push({
            stage: "actionflow",
            level: "error",
            file: "flow.json",
            rule: "actionflow-setstate-localstate-prefix",
            message: `Flow '${flowName}' step[${i}] setState target '${target}' must NOT use 'localState.' prefix — use '${stripped}' (bare key).`,
          })
        }
      }
    }

    // Layer-2 validate-action contract (RETEST6 §4.2) — target must be a
    // symbol path; rule entries must carry the executor's per-type keys.
    if (canonicalAction === "validate") {
      analyzeValidateStep(issues, flowName, i, step)
    }

    // Check expression references in params
    const params = step.params as Record<string, unknown> | undefined
    if (params && typeof params === "object") {
      for (const [key, val] of Object.entries(params)) {
        if (typeof val !== "string" || !val.includes("#{")) continue
        const matches = val.match(/#\{([^}]+)\}/g)
        if (!matches) continue
        for (const m of matches) {
          let body = m.slice(2, -1).trim()
          // Strip leading negation
          body = body.replace(/^!+/, "").trim()
          // Skip complex expressions (function calls, ternary, operators)
          if (/^[a-zA-Z_]\w*\s*\(/.test(body) || /[?:+\-*/<>=&|]/.test(body)) continue
          // Extract namespace (first identifier)
          const nsMatch = body.match(/^#?([a-zA-Z_][a-zA-Z0-9_]*)/)
          if (!nsMatch) continue
          const ns = nsMatch[1]
          if (!knownNamespaces.has(ns)) {
            issues.push({
              stage: "actionflow",
              level: "warning",
              file: "flow.json",
              rule: "actionflow-unresolved-ref",
              message: `Flow '${flowName}' step[${i}] param '${key}' references unknown namespace '${ns}'`,
            })
          }
        }
      }
    }

    forEachNestedStepBranch(step, (nested) => {
      analyzeSteps(issues, flowName, nested, knownNamespaces)
    })
  }
}

function analyzeExpressionPrefixes(
  issues: ValidationIssue[],
  flowName: string,
  steps: Record<string, unknown>[],
): void {
  const walk = (steps: Record<string, unknown>[], pathPrefix: string) => {
    steps.forEach((step, i) => {
      const stepPath = `${pathPrefix}[${i}]`
      const params = step.params as Record<string, unknown> | undefined
      if (params) {
        for (const [k, v] of Object.entries(params)) {
          if (typeof v !== "string") continue
          const scan = scanExpressionPrefixes(v)
          for (const legacy of scan.legacy) {
            issues.push({
              stage: "actionflow",
              level: "error",
              file: "flow.json",
              rule: "expression-prefix-legacy",
              path: `${stepPath}.params.${k}`,
              message: `Legacy expression prefix "${legacy}." found: ${LEGACY_EXPR_PREFIXES[legacy]}. Use one of 9 canonical (local, event, props, item, _results, computed, appData, env, route).`,
            })
          }
        }
      }
      forEachNestedStepBranch(step, (nested, branchPath) => {
        walk(nested, `${stepPath}.${branchPath}`)
      })
    })
  }
  walk(steps, `flows.${flowName}.steps`)
}

function walkUiTree(
  node: Record<string, unknown>,
  cb: (node: Record<string, unknown>) => void,
): void {
  cb(node)
  const children = node.children as Record<string, unknown>[] | undefined
  if (Array.isArray(children)) {
    for (const child of children) {
      if (child && typeof child === "object") walkUiTree(child, cb)
    }
  }
  const template = node.template
  if (template && typeof template === "object" && !Array.isArray(template)) {
    walkUiTree(template as Record<string, unknown>, cb)
  }
}

// ── Connection Validation Rules ──

/**
 * Collect every flow name that `events.json` already wires as a UI/lifecycle
 * trigger target — `elements.*.<eventType>.flow`, the flat `bindings` map,
 * the v2 top-level element-map form (all three folded by
 * `normalizeEventBindings`), plus `lifecycle.*.flow` (not covered by that
 * normalizer, added here). A flow name in this set is driven by an actual
 * UI tap/change handler or a lifecycle hook, not by an in-block `emitEvent`.
 *
 * Known over-collection risk (M6-R1 fix-round-1, 2026-08-11, adversarial
 * review M4 — `.tmp-m6r1-adversarial-review.md`, recorded not fixed: no
 * corpus instance triggers it, and the safe general fix requires
 * `normalizeEventBindings` itself to stop treating "any `events` key other
 * than bindings/elements/lifecycle" as an element-map, which is a wider
 * change than this rule owns). `normalizeEventBindings`'s v2 top-level scan
 * (`utils/event-bindings.ts`) treats EVERY key under `events` other than
 * `bindings`/`elements`/`lifecycle` as an element-id → event-map entry. If
 * `events.json` ever carried an unrelated metadata key whose value
 * happened to contain a `.flow` field (e.g. `{meta: {x: {flow:
 * "someFlow"}}}`), that flow name would be wrongly added here, suppressing
 * a genuine `actionflow-listener-no-emitter` warning for it. Narrow in
 * practice — this generation path has never been observed to emit such a
 * key — but the discriminant is entirely dependent on this collected set,
 * so it is worth naming precisely rather than only informally.
 */
function collectEventWiredFlowNames(bundle: BlockBundle): Set<string> {
  const names = new Set<string>()
  const addFlowRef = (binding: unknown): void => {
    if (!binding || typeof binding !== "object") return
    const flowRef = (binding as Record<string, unknown>).flow
    if (typeof flowRef === "string" && flowRef) names.add(flowRef)
  }
  for (const binding of Object.values(normalizeEventBindings(bundle))) addFlowRef(binding)
  const events = bundle.events as Record<string, unknown> | undefined
  const lifecycle = events?.lifecycle as Record<string, unknown> | undefined
  if (lifecycle && typeof lifecycle === "object" && !Array.isArray(lifecycle)) {
    for (const hookBinding of Object.values(lifecycle)) addFlowRef(hookBinding)
  }
  return names
}

/**
 * Hygiene (advisory): an `on:` app-event listener should have an in-block `emitEvent` that can
 * start it. Advisory only (the emitter may be cross-block or the host bus). We deliberately do
 * NOT flag the reverse (emitEvent with no in-block on: listener): emitting to the host
 * AppEventBus with no in-block subscriber is the NORMAL case (component/host consumers), so
 * flagging it is pure noise. Mirrors the collect-then-flag shape of validateCallFlowRefWithinBlock.
 *
 * Scope correction (M6-L1 attribution, 2026-08-10 —
 * docs/superpowers/reports/2026-08-10-m7-attribution-loops/M6-L1-attribution.md):
 * this rule's premise — "`on:` is a pub/sub app-event channel name" — does not
 * hold whenever `events.json` already references this same flow BY NAME
 * (`elements.*.flow` or `lifecycle.*.flow`). In that generation shape `on:`
 * is a self-identifying trigger key (e.g. `"btn-approve-tap"`,
 * `"expense-summary-init"`), not an app-event channel, and the real wiring is
 * the events.json reference — not an in-block `emitEvent`. 5/5 initially
 * sampled instances carried `trigger:"event"`/`"lifecycle"` alongside this
 * pattern, but the corpus ALSO has the identical events.json-wired shape
 * with NO `trigger` field at all (`expense-summary`'s tile-tap flows: only
 * `on:` is set, yet `events.json.elements.pending-tile.tap.flow` names them
 * directly) — discovered rescoring the repaired validator against the full
 * BASELINE-N10 corpus, not from the original 5-sample check. Gating on
 * `trigger` would have left that shape flagged, so the discriminant is
 * "does events.json reference this flow by name" alone, not the presence or
 * value of `trigger`. The corpus never emits `emitEvent`/`emit` at all, so
 * this is not merely narrowing a heuristic — it removes a premise that does
 * not hold for this generation path's `on:` usage in general.
 *
 * Known, accepted false negative (M6-R1 fix-round-1, 2026-08-11, adversarial
 * review M5): a flow that is BOTH wired via `events.json` (a UI tap/
 * lifecycle target) AND genuinely subscribes to a real pub/sub app-event
 * channel via the SAME `on:` value is skipped entirely by the check above —
 * an in-block-emitter gap for the pub/sub half is not caught, because the
 * events.json wiring alone is enough to skip the whole flow. Accepted
 * trade-off: the corpus has 0 `emitEvent`/`emit` occurrences (confirmed
 * above), so this has no measured impact today, and a flow reachable via a
 * real UI handler is not "orphaned" in the sense this rule exists to catch
 * even if its OTHER trigger path is unwired.
 */
function validateEmitListenerCoherence(
  issues: ValidationIssue[],
  flows: Record<string, unknown>,
  bundle: BlockBundle,
): void {
  const emitted = new Set<string>()
  const listened = new Map<string, string>() // eventName → first subscribing flow
  const collectEmits = (steps: unknown): void => {
    if (!Array.isArray(steps)) return
    for (const s of steps) {
      if (!s || typeof s !== "object") continue
      const step = s as Record<string, unknown>
      const resolvedAction = typeof step.action === "string" ? resolveRuntimeAction(step.action) : undefined
      if (resolvedAction?.canonicalAction === "emitEvent") {
        const p = step.params as Record<string, unknown> | undefined
        const name = (p?.event ?? p?.eventName ?? p?.name) as unknown
        if (typeof name === "string" && name) emitted.add(name)
      }
      for (const nested of ["then", "else", "steps", "try", "catch", "finally", "default", "onEnter", "onSuccess", "onError", "onFinally", "compensate", "stages"] as const) collectEmits(step[nested])
      if (step.cases && typeof step.cases === "object") {
        for (const b of Object.values(step.cases as Record<string, unknown>)) collectEmits(b)
      }
    }
  }
  const wiredFlowNames = collectEventWiredFlowNames(bundle)
  for (const [flowName, flowDef] of Object.entries(flows)) {
    if (!flowDef || typeof flowDef !== "object") continue
    const fd = flowDef as Record<string, unknown>
    if (typeof fd.on === "string" && fd.on && wiredFlowNames.has(flowName)) {
      collectEmits(fd.steps)
      continue
    }
    if (typeof fd.on === "string" && fd.on && !listened.has(fd.on)) listened.set(fd.on, flowName)
    collectEmits(fd.steps)
  }
  for (const [name, flowName] of listened) {
    if (!emitted.has(name)) {
      issues.push({ stage: "actionflow", level: "warning", file: "flow.json", rule: "actionflow-listener-no-emitter", message: `Flow '${flowName}' subscribes to app-event '${name}' (on:) but no flow in this block emits it via emitEvent. If it is driven by the host bus or another block, ignore; otherwise the loop will never start.` })
    }
  }
}

/**
 * Validate that event bindings reference flows that exist in flow.json.
 * Catches: event → non-existent flow name
 */
function validateFlowEventBindings(
  issues: ValidationIssue[],
  flows: Record<string, unknown>,
  bundle: BlockBundle,
): void {
  const flowNames = new Set(Object.keys(flows))
  const events = (bundle.events as Record<string, unknown>)?.bindings as Record<string, unknown> | undefined
  if (!events || typeof events !== "object") return

  for (const [eventKey, binding] of Object.entries(events)) {
    if (!binding || typeof binding !== "object") continue
    const flowRef = (binding as Record<string, unknown>).flow as string | undefined
    if (!flowRef) continue

    // Skip cross-block references (blockName.flowName)
    if (flowRef.includes(".")) continue

    if (!flowNames.has(flowRef)) {
      issues.push({
        stage: "actionflow",
        level: "error",
        file: "events.json",
        rule: "actionflow-event-flow-missing",
        message: `Event '${eventKey}' references flow '${flowRef}' which does not exist in flow.json. Available: ${Array.from(flowNames).join(", ")}`,
      })
    }
  }
}

/**
 * Validate that callFlow steps reference flows that exist.
 * Catches: callFlow → non-existent flow name (within same block)
 */
function validateCallFlowReferences(
  issues: ValidationIssue[],
  flows: Record<string, unknown>,
): void {
  const flowNames = new Set(Object.keys(flows))

  for (const [flowName, flowDef] of Object.entries(flows)) {
    if (typeof flowDef !== "object" || flowDef === null) continue
    const steps = (flowDef as Record<string, unknown>).steps
    if (!Array.isArray(steps)) continue

    collectCallFlowRefs(steps as Record<string, unknown>[], flowName, flowNames, issues)
  }
}

function collectCallFlowRefs(
  steps: Record<string, unknown>[],
  parentFlowName: string,
  flowNames: Set<string>,
  issues: ValidationIssue[],
): void {
  for (let i = 0; i < steps.length; i++) {
    const step = steps[i]
    if (!step || typeof step !== "object") continue

    if (step.action === "callFlow") {
      const params = step.params as Record<string, unknown> | undefined
      const ref = (params?.flow ?? params?.flowName) as string | undefined
      if (ref && !ref.includes(".") && !flowNames.has(ref)) {
        issues.push({
          stage: "actionflow",
          level: "error",
          file: "flow.json",
          rule: "actionflow-callflow-not-found",
          message: `Flow '${parentFlowName}' step[${i}] callFlow references '${ref}' which does not exist. Available: ${Array.from(flowNames).join(", ")}`,
        })
      }
    }

    // Recurse into nested steps
    for (const nested of ["then", "else", "steps", "try", "catch", "finally", "onEnter", "onSuccess", "onError", "onFinally", "compensate", "stages"] as const) {
      if (Array.isArray(step[nested])) {
        collectCallFlowRefs(step[nested] as Record<string, unknown>[], parentFlowName, flowNames, issues)
      }
    }
    // Switch cases
    const cases = step.cases as Record<string, unknown[]> | undefined
    if (cases && typeof cases === "object") {
      for (const branch of Object.values(cases)) {
        if (Array.isArray(branch)) {
          collectCallFlowRefs(branch as Record<string, unknown>[], parentFlowName, flowNames, issues)
        }
      }
    }
    if (Array.isArray(step.default)) {
      collectCallFlowRefs(step.default as Record<string, unknown>[], parentFlowName, flowNames, issues)
    }
  }
}

/**
 * Wave 11C-followup — unified within-block callFlow target existence rule.
 *
 * Warns (first emission, severity warning) when a callFlow target name does not
 * exist in this block's flow.cogo.json. Scans three shapes (some speculative,
 * some canonical — together this is the umbrella rule referenced in OPEN-QUESTIONS
 * Q-C1 body):
 *   (a) events.cogo.json bindings: `{ "<evt>:<name>": { "callFlow": "flowName" } }`
 *   (b) events.cogo.json bindings: `{ "<evt>:<name>": { "type": "callFlow", "flow": "flowName" } }`
 *   (c) flow.cogo.json step action: `{ "action": "callFlow", "params": { "flow"|"flowName": "flowName" } }`
 *
 * Dotted form `<block>.<flow>` is intentionally SKIPPED — cross-block ref; deferred
 * pending first corpus consumer (0 corpus cross-block callFlow uses per Wave 11C
 * brainstorm).
 *
 * Co-exists with `actionflow-event-flow-missing` (error, canonical `{ flow }` shape
 * in event bindings) and `actionflow-callflow-not-found` (error, step action with
 * valid `flow`/`flowName` param). This warning adds a defense layer for alternate
 * callFlow-shaped refs that the existing error rules don't cover.
 */
function validateCallFlowRefWithinBlock(
  issues: ValidationIssue[],
  flows: Record<string, unknown>,
  bundle: BlockBundle,
): void {
  const flowNames = new Set(Object.keys(flows))

  const emit = (file: "events.json" | "flow.json", path: string, ref: string): void => {
    issues.push({
      stage: "actionflow",
      level: "warning",
      file,
      rule: "callflow-flow-ref-within-block",
      path,
      message: `callFlow target '${ref}' not defined in this block's flow.cogo.json`,
    })
  }

  // (a) + (b) — events.cogo.json bindings
  const bindings = (bundle.events as Record<string, unknown>)?.bindings as Record<string, unknown> | undefined
  if (bindings && typeof bindings === "object") {
    for (const [evtKey, binding] of Object.entries(bindings)) {
      if (!binding || typeof binding !== "object") continue
      const b = binding as Record<string, unknown>

      // Shape (a): { callFlow: "flowName" }
      const shorthand = b.callFlow
      if (typeof shorthand === "string") {
        if (!shorthand.includes(".") && !flowNames.has(shorthand)) {
          emit("events.json", `bindings["${evtKey}"].callFlow`, shorthand)
        }
      }

      // Shape (b): { type: "callFlow", flow: "flowName" }
      if (b.type === "callFlow") {
        const ref = (b.flow ?? b.flowName) as string | undefined
        if (typeof ref === "string" && !ref.includes(".") && !flowNames.has(ref)) {
          emit("events.json", `bindings["${evtKey}"].flow`, ref)
        }
      }
    }
  }

  // (c) — flow.cogo.json step action callFlow (walks nested branches)
  for (const [parentFlowName, flowDef] of Object.entries(flows)) {
    if (typeof flowDef !== "object" || flowDef === null) continue
    const steps = (flowDef as Record<string, unknown>).steps
    if (!Array.isArray(steps)) continue
    walkCallFlowSteps(steps as Record<string, unknown>[], `flows.${parentFlowName}.steps`, flowNames, emit)
  }
}

function walkCallFlowSteps(
  steps: Record<string, unknown>[],
  pathPrefix: string,
  flowNames: Set<string>,
  emit: (file: "events.json" | "flow.json", path: string, ref: string) => void,
): void {
  for (let i = 0; i < steps.length; i++) {
    const step = steps[i]
    if (!step || typeof step !== "object") continue
    const stepPath = `${pathPrefix}[${i}]`

    if (step.action === "callFlow") {
      const params = step.params as Record<string, unknown> | undefined
      const ref = (params?.flow ?? params?.flowName) as string | undefined
      if (typeof ref === "string" && !ref.includes(".") && !flowNames.has(ref)) {
        emit("flow.json", `${stepPath}.params.flow`, ref)
      }
    }

    // Recurse into nested step arrays
    for (const nested of ["then", "else", "steps", "try", "catch", "finally", "onEnter", "onSuccess", "onError", "onFinally", "compensate", "stages"] as const) {
      if (Array.isArray(step[nested])) {
        walkCallFlowSteps(step[nested] as Record<string, unknown>[], `${stepPath}.${nested}`, flowNames, emit)
      }
    }
    const cases = step.cases as Record<string, unknown[]> | undefined
    if (cases && typeof cases === "object") {
      for (const [caseKey, branch] of Object.entries(cases)) {
        if (Array.isArray(branch)) {
          walkCallFlowSteps(branch as Record<string, unknown>[], `${stepPath}.cases.${caseKey}`, flowNames, emit)
        }
      }
    }
    if (Array.isArray(step.default)) {
      walkCallFlowSteps(step.default as Record<string, unknown>[], `${stepPath}.default`, flowNames, emit)
    }
  }
}

/**
 * Validate that _results targets used in subsequent steps were actually set by a prior step.
 * Catches: referencing #{_results.api.data} without a preceding restApi with resultTarget
 */
function validateResultTargetUsage(
  issues: ValidationIssue[],
  flows: Record<string, unknown>,
): void {
  // `_results` is BLOCK-scoped, not flow-scoped. The auto-capture writes
  // through `this.state.setState('_results.'+step.name, result)` on the
  // engine's single StateManager (action-flow-engine.ts:1375-1378), and
  // `executeFlow` never clears or re-scopes it — so a name bound by the
  // lifecycle flow is readable from an event flow, which is the corpus's
  // dominant idiom. Seed each flow with what the OTHER flows bind; ordering
  // WITHIN a flow stays enforced exactly as before (a flow is never seeded
  // with its own bindings).
  const boundPerFlow = new Map<string, Set<string>>()
  for (const [flowName, flowDef] of Object.entries(flows)) {
    if (typeof flowDef !== "object" || flowDef === null) continue
    const steps = (flowDef as Record<string, unknown>).steps
    if (!Array.isArray(steps)) continue
    const bound = new Set<string>()
    // Discard mode: collect bindings without emitting issues.
    trackResultTargets(steps as Record<string, unknown>[], flowName, bound, [])
    boundPerFlow.set(flowName, bound)
  }

  for (const [flowName, flowDef] of Object.entries(flows)) {
    if (typeof flowDef !== "object" || flowDef === null) continue
    const steps = (flowDef as Record<string, unknown>).steps
    if (!Array.isArray(steps)) continue

    // Slots the runtime writes on a fixed path, present regardless of what
    // this flow declares (geminiFunctionCall -> _results.agentHistory,
    // supabase realtime -> _results.realtimePayload).
    const definedResults = new Set<string>(UNCONDITIONAL_RESULT_SLOTS)
    for (const [otherName, bound] of boundPerFlow) {
      if (otherName === flowName) continue
      for (const name of bound) definedResults.add(name)
    }
    trackResultTargets(steps as Record<string, unknown>[], flowName, definedResults, issues)
  }
}

// The runtime's `_results.<name>` write surface is DERIVED, not mirrored.
// Two projections, both kept: `vocab/runtime-result-writes` (ontology JSON
// regenerated from runtime-v2 source) and `vocab/runtime-step-vocabulary`
// (`RESULT_AUTO_CAPTURE_ACTIONS` via @cogo/action-registry catalog).

function trackResultTargets(
  steps: Record<string, unknown>[],
  flowName: string,
  definedResults: Set<string>,
  issues: ValidationIssue[],
): void {
  for (let i = 0; i < steps.length; i++) {
    const step = steps[i]
    if (!step || typeof step !== "object") continue

    const params = step.params as Record<string, unknown> | undefined

    // A step that NAMES its own `_results.` slot defines it. Derived
    // spellings (`resultTarget`, `resultPath`, `responseKey`, ...) come from
    // the engine's target-role declarations — see vocab/runtime-result-writes.
    // Only `resultTarget` was read here before, so `fileUpload`'s `resultPath`
    // suppressed the default slot and defined nothing in its place.
    const namedSlot = namedResultSlot(params)
    if (namedSlot) {
      definedResults.add(namedSlot)
      const top = namedSlot.split(".")[0]
      if (top) definedResults.add(top)
    }

    // Track resultTarget definitions
    if (params?.resultTarget) {
      const target = String(params.resultTarget).replace(/^#/, "")
      definedResults.add(target)
      // Also add the top-level namespace (e.g., "_results" from "_results.checkout")
      const topLevel = target.split(".")[0]
      if (topLevel) definedResults.add(topLevel)
    }

    // Runtime write path (a), derived: every state-target address this step's
    // action contract declares — the per-action target param AND its declared
    // read aliases (`restApi` also honours target/responseKey/resultKey,
    // `storage` spells it `resultPath`), the `defaultTarget` written when no
    // target param is present (`payment` → `_results.payment`), and the
    // param-composed default (`supabaseQuery` → `_results.<table>`). Lifted
    // sibling keys are included for source-typed steps. Purely ADDITIVE: the
    // unconditional `params.resultTarget` acceptance above is untouched.
    for (const address of declaredStateTargets(step)) definedResults.add(address)

    // Check `_results.X` reads in params — including nested object values
    // (op2c-name 2026-08-21: `setState.value.acctNm: "#{_results.maskedName}"`
    // after compute `name: maskName` / `target: maskedName` was invisible to
    // the string-only walker, so the safety retry never fired). Same consumer
    // as the condition gate below: ActionFlowEngine writes
    // `_results.${step.name}` after the handler returns, never params.target.
    // Error (not warning): this stage is safety:true; warnings cannot retry.
    // Deliberately BEFORE step.name auto-capture (adversarial-review C1 case D).
    if (params && typeof params === "object") {
      const strings: Array<{ key: string; val: string }> = []
      const walk = (node: unknown, key: string): void => {
        if (typeof node === "string") {
          strings.push({ key, val: node })
          return
        }
        if (Array.isArray(node)) {
          node.forEach((child) => walk(child, key))
          return
        }
        if (node && typeof node === "object") {
          for (const [k, v] of Object.entries(node as Record<string, unknown>)) {
            if (k === "resultTarget") continue
            walk(v, k)
          }
        }
      }
      walk(params, "")
      const seen = new Set<string>()
      for (const { key, val } of strings) {
        if (!val.includes("_results")) continue
        const refs = val.match(/_results\.([A-Za-z_]\w*)/g)
        if (!refs) continue
        for (const ref of refs) {
          const dedupe = `${key}\0${ref}`
          if (seen.has(dedupe)) continue
          seen.add(dedupe)
          const resultKey = `#{${ref}}`
          const topKey = `_results.${ref.split(".")[1] ?? ""}`
          if (!definedResults.has(topKey) && !definedResults.has(ref) && !definedResults.has(resultKey)) {
            issues.push({
              stage: "actionflow",
              level: "error",
              file: "flow.json",
              rule: "actionflow-result-not-defined",
              message:
                `Flow '${flowName}' step[${i}] param '${key}' references '${resultKey}' ` +
                `but no prior value-returning step auto-captures that name ` +
                `(runtime writes _results.<step.name>, not params.target).`,
            })
          }
        }
      }
    }

    // Conditional `condition` is not a param — the runtime still reads it
    // (executeConditional). Auto-capture writes `_results.${step.name}`
    // (action-flow-engine.ts executeBasic), never `params.target`. A gate
    // on `#{!_results.foundAccount}` after `compute name=findAccount
    // target=foundAccount` therefore inverts a successful find (xi-01).
    // Error (not warning): this stage is safety:true; warnings cannot retry.
    if (typeof step.condition === "string" && step.condition.includes("_results")) {
      // `#{!_results.X}` — the bang sits between `#{` and `_results.`, so the
      // params scanner's `/#\{_results\./` pattern never matches. The
      // runtime still reads this condition (executeConditional).
      const refs = step.condition.match(/_results\.([A-Za-z_]\w*)/g)
      if (refs) {
        for (const ref of refs) {
          const resultPath = ref
          const resultKey = ref.startsWith("_results.") ? `#{${ref}}` : ref
          const topKey = `_results.${ref.split(".")[1] ?? ""}`
          if (!definedResults.has(topKey) && !definedResults.has(resultPath) && !definedResults.has(`#{${ref}}`)) {
            issues.push({
              stage: "actionflow",
              level: "error",
              file: "flow.json",
              rule: "actionflow-result-not-defined",
              message:
                `Flow '${flowName}' step[${i}] condition references '${resultKey}' ` +
                `but no prior value-returning step auto-captures that name ` +
                `(runtime writes _results.<step.name>, not params.target).`,
            })
          }
        }
      }
    }

    // Runtime step.name auto-capture (see the derived write-surface note
    // above) — gated to the actions whose handler can actually return
    // non-undefined, and only when the step IS an action
    // invocation (`step.action` present) rather than a control-flow node
    // (e.g. `type:"conditional"`) that merely happens to carry a `name`.
    // This does NOT suppress a genuine mismatch: a reference to
    // `_results.<other-name>` that isn't this (or any prior) step's own
    // name is still flagged — the confirmed real defect this repair must
    // keep catching is `computePendingStats` (step.name) vs
    // `_results.pendingStats` (reference) never coinciding; `pendingStats`
    // is that step's `params.target`, a bare state write, not its name.
    if (
      typeof step.name === "string" && step.name &&
      typeof step.action === "string" &&
      AUTO_CAPTURE_ACTIONS.has(resolveRuntimeAction(step.action)?.canonicalAction ?? step.action)
    ) {
      definedResults.add(`_results.${step.name}`)
    }

    // Default-slot write: an executor that takes no target param still writes
    // its own fallback slot (pickFiles -> _results.pickFiles). The step never
    // names it, so nothing above sees it.
    if (typeof step.action === "string") {
      const canonical = resolveRuntimeAction(step.action)?.canonicalAction ?? step.action
      const slot = DEFAULT_RESULT_SLOTS[canonical]
      if (slot && !RESULT_TARGET_PARAMS.some((k) => params?.[k] != null)) {
        definedResults.add(slot)
      }
    }

    if (typeof step.name === "string" && step.name && autoCapturesStepName(step)) {
      definedResults.add(`_results.${step.name}`)
    }

    // A `parallel` step's branches are awaited via `Promise.all`
    // (action-flow-engine.ts:1513-1515 `await Promise.all(step.steps.map(...))`)
    // against the ONE shared StateManager, and a rejection aborts the flow —
    // so on every path that reaches the next outer step, every branch has
    // completed and its `_results` writes are visible. Same guarantee as
    // `finally` below, and unlike `loop.steps` (may iterate zero times) or
    // `stage.steps` (may divert to onError/compensate), which share the key
    // name but not the semantics.
    if (step.type === "parallel" && Array.isArray(step.steps)) {
      trackResultTargets(step.steps as Record<string, unknown>[], flowName, definedResults, issues)
    }

    // Conditional/iterated branches do not guarantee their result definitions
    // to the outer sequence, so analyze them against an isolated snapshot...
    const branchOutcomes: Array<Set<string>> = []
    for (const nested of ["then", "else", "steps", "try", "catch"] as const) {
      if (nested === "steps" && step.type === "parallel") continue
      if (Array.isArray(step[nested])) {
        const snapshot = new Set(definedResults)
        trackResultTargets(step[nested] as Record<string, unknown>[], flowName, snapshot, issues)
        if (nested === "then" || nested === "else") branchOutcomes.push(snapshot)
      }
    }

    // ...but a conditional with BOTH arms present is total: whichever way it
    // goes, execution continues past it, so anything defined in every arm is
    // defined on every path that reaches the next outer step. That is the same
    // argument `finally` gets below, and it was missing here — a flow that
    // called restApi with the same resultTarget in `then` and in `else` was
    // told its result did not exist. One arm is NOT enough, and a bare `then`
    // contributes nothing.
    if (
      Array.isArray(step.then) && Array.isArray(step.else) &&
      branchOutcomes.length === 2
    ) {
      for (const name of branchOutcomes[0]) {
        if (branchOutcomes[1].has(name)) definedResults.add(name)
      }
    }

    // A finally branch always completes before execution can reach the next
    // outer step. Therefore any result it defines is guaranteed on every path
    // that continues, and must be visible to the remainder of this sequence.
    if (Array.isArray(step.finally)) {
      trackResultTargets(step.finally as Record<string, unknown>[], flowName, definedResults, issues)
    }
  }
}

/**
 * Validate that action types in basic steps are registered in ACTION_DEFS.
 * Catches: typos in action names, unknown custom actions
 */
function validateActionTypeValid(
  issues: ValidationIssue[],
  flows: Record<string, unknown>,
): void {
  for (const [flowName, flowDef] of Object.entries(flows)) {
    if (typeof flowDef !== "object" || flowDef === null) continue
    const steps = (flowDef as Record<string, unknown>).steps
    if (!Array.isArray(steps)) continue

    checkActionTypes(steps as Record<string, unknown>[], flowName, issues)
  }
}

function checkActionTypes(
  steps: Record<string, unknown>[],
  flowName: string,
  issues: ValidationIssue[],
): void {
  for (let i = 0; i < steps.length; i++) {
    const step = steps[i]
    if (!step || typeof step !== "object") continue

    const effectiveType = step.type ?? (step.action ? "basic" : undefined)
    // A step whose `type` IS an action name dispatches that action
    // (action-flow-engine.ts:1250-1255 `isSourceTypedActionStep` →
    // `sourceTypedStepToBasic`), so it is an action invocation for every check
    // below, and its argument surface is the whole step, not `step.params`.
    const sourceTyped = typeof step.type === "string" && isSourceTypedActionStepType(step.type)
    const action = stepDispatchedAction(step)

    if ((effectiveType === "basic" || effectiveType === "expression" || effectiveType === "action" || sourceTyped) && action) {
      const resolvedAction = resolveRuntimeAction(action)
      const canonicalAction = resolvedAction?.canonicalAction ?? action
      // Also accept standalone actions (upsertWhere, removeWhere, etc.) used in spec patterns
      const specActions = new Set(["upsertWhere", "removeWhere", "updateWhere", "compute", "scrollToPosition"])
      if (resolvedAction?.isAlias) {
        issues.push({
          stage: "actionflow",
          level: "warning",
          file: "flow.json",
          rule: "actionflow-legacy-action-alias",
          message: `Flow '${flowName}' step[${i}] uses legacy action alias '${action}'. Use canonical '${canonicalAction}' in new flows.`,
        })
      }
      if (!ACTION_DEFS[canonicalAction] && !specActions.has(canonicalAction) && !resolvedAction) {
        issues.push({
          stage: "actionflow",
          level: "warning",
          file: "flow.json",
          rule: "actionflow-unknown-action",
          message: `Flow '${flowName}' step[${i}] uses unknown action '${action}'. Check spelling or register in action-registry.`,
        })
      } else if (ACTION_DEFS[canonicalAction]) {
        const params = stepArgumentSurface(step)
        validateActionParamEnums(
          issues,
          ACTION_DEFS[canonicalAction],
          params,
          `Flow '${flowName}' step[${i}]`,
          "flow.json",
        )
      }
    }

    // Validate step type is valid. The accepted set is the runtime's, not a
    // list kept beside this rule: the structural control-flow types
    // `executeStep` switches on, PLUS every name `resolveBuiltin` answers to
    // (`isSourceTypedActionStep`, action-flow-engine.ts:1260-1262) — canonical
    // action names and runtime aliases alike. See vocab/runtime-step-vocabulary.ts.
    if (step.type && !isRuntimeStepType(step.type as string)) {
      issues.push({
        stage: "actionflow",
        level: "error",
        file: "flow.json",
        rule: "actionflow-invalid-step-type",
        message:
          `Flow '${flowName}' step[${i}] has invalid type '${step.type}'. ` +
          `Valid: ${Array.from(STRUCTURAL_STEP_TYPES).join(", ")}, or any runtime action name.`,
      })
    }

    // Recurse
    for (const nested of ["then", "else", "steps", "try", "catch", "finally", "onEnter", "onSuccess", "onError", "onFinally", "compensate", "stages"] as const) {
      if (Array.isArray(step[nested])) {
        checkActionTypes(step[nested] as Record<string, unknown>[], flowName, issues)
      }
    }
    const cases = step.cases as Record<string, unknown[]> | undefined
    if (cases && typeof cases === "object") {
      for (const branch of Object.values(cases)) {
        if (Array.isArray(branch)) checkActionTypes(branch as Record<string, unknown>[], flowName, issues)
      }
    }
    if (Array.isArray(step.default)) {
      checkActionTypes(step.default as Record<string, unknown>[], flowName, issues)
    }
  }
}
